import { Injectable, ViewContainerRef } from '@angular/core';
import { User } from '../models/user';
import { UserApiService } from '../../services/user/user-api.service';
import { PreAuthDto } from '../../services/user/dto/pre-auth-dto';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { PreAuthResponseDto } from '../../services/user/dto/pre-auth-response-dto';
import { RegisterDto } from '../../services/user/dto/register-dto';
import { RegisterResponseDto } from '../../services/user/dto/register-response-dto';
import { LoginResponseDto } from '../../services/user/dto/login-response-dto';
import { LoginDto } from '../../services/user/dto/login-dto';
import { UpdateProfileDto } from '../../services/user/dto/update-profile-dto';
import { ApiResponseDto } from '../../services/tables/dto/api-response-dto';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { AuthModalComponent } from '../../components/auth-modal/auth-modal.component';

export type AuthStep = 'AUTHORIZED' | 'UNAUTHORIZED';
export type OriginType = 'PAY' | 'WALLET' | 'RECEIPT' | 'MENU';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  origin: OriginType = 'PAY';

  /** Subject para emitir mudanças no estado de autenticação */
  $authState: BehaviorSubject<AuthStep>;

  constructor(
    private userApiService: UserApiService,
    private localStorageService: LocalStorageService,
    private ngxSmartModalService: NgxSmartModalService,
  ) {
    // Inicializa o estado de autenticação baseado no localStorage
    const initialAuthState = this.checkUser();
    this.$authState = new BehaviorSubject<AuthStep>(initialAuthState);
  }

  createAuthModal(viewContainerRef: ViewContainerRef): NgxSmartModalComponent {
    return this.ngxSmartModalService.create(
      'auth-modal',
      AuthModalComponent,
      viewContainerRef,
      {
        customClass: 'dialog-small',
      }
    );
  }

  closeAuthModal(): void {
    this.ngxSmartModalService.getModal('auth-modal')?.close();
  }

  checkUser(): AuthStep {
    if(this.hasUser()) {
      const user = this.getUser();
      if(user?.isExpired()) {
        return 'UNAUTHORIZED';
      } else {
        return 'AUTHORIZED';
      }
    } else {
      return 'UNAUTHORIZED';
    }
  }

  hasUser(): boolean {
    return this.localStorageService.hasItem('user');
  }

  getUser(): User | null {
    const user = this.localStorageService.getItem<User>('user');

    if(user) {
      return new User(user.id, user.accessToken, user.expiresIn, user.name, user.clientId, user.tableCartId, user.expiredDate);
    }

    return null;
  }

  setUser(user: User) {
    this.localStorageService.setItem('user', user, LocalStorageService.timeToMs.hours(24));
  }

  setOrigin(origin: OriginType) {
    this.origin = origin;
  }

  getOrigin(): OriginType {
    return this.origin;
  }

  preAuth(countryCode: string, phone: string, idi: number): Observable<PreAuthResponseDto> {
    const data = new PreAuthDto(countryCode, phone, idi);
    return this.userApiService.preAuth(data)
  }

  resendSms(countryCode: string, phone: string): Observable<ApiResponseDto> {
    return this.userApiService.resendSms(countryCode, phone);
  }

  register(countryCode: string, phone: string, name: string, idi: number, tableId: number): Observable<RegisterResponseDto> {
    const data = new RegisterDto(countryCode, phone, name, idi, tableId);
    return this.userApiService.register(data);
  }

  logout(): void {
    this.localStorageService.removeItem('user');
    this.$authState.next('UNAUTHORIZED');
  }

  updateProfile(phone: string, idi: number, email: string, data_nascimento?: string, clientId?: number): Observable<any> {
    const dto = new UpdateProfileDto(phone, idi, email, data_nascimento, clientId);
    return this.userApiService.updateProfile(dto);
  }

  login(countryCode: string, phone: string, idi:number, tableCartId: number): Observable<LoginResponseDto> {
    const data = new LoginDto(countryCode, phone, idi, tableCartId);
    return this.userApiService.login(data).pipe(tap((res: LoginResponseDto) => {
      this.setUser(new User(res.data.id, res.data.access_token, res.data.expires_in, res.data.name, res.data.clientId, res.data.tableCartId));
      this.$authState.next('AUTHORIZED');
      return res;
    }));
  }
}
